import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  usePolling,
  UsePollingParams,
  useUnderEditContext,
} from '@neptune/shared/common-business-logic';
import { BaseLeaderboardEntry, LeaderboardListModel } from '@neptune/shared/leaderboard-domain';

import { Merge } from 'common/utility-types';
import { AppState } from 'state/types';

import {
  fetchLeaderboard,
  fetchLeaderboardAbort,
  fetchLeaderboardFail,
  fetchLeaderboardSuccess,
} from './redux/leaderboards-actions';
import { LeaderboardState } from './redux/leaderboards-reducer';
import { getLeaderboardState } from './redux/leaderboards-selectors';

export const useLeaderboardPolling = <Q, T extends BaseLeaderboardEntry>({
  id,
  query,
  resolver,
  pollingInterval,
  enabled = true,
}: {
  id: string;
  query: Q;
  resolver: (query: Q) => Promise<LeaderboardListModel<T>>;
  pollingInterval?: number;
  enabled?: boolean;
}): Merge<LeaderboardState<T>, { refresh: () => void }> => {
  const dispatch = useDispatch();

  const { underEdit } = useUnderEditContext();

  const handleFetchSuccess = React.useCallback(
    (data: LeaderboardListModel<T>, query: Q) =>
      dispatch(fetchLeaderboardSuccess(id, { data, query })),
    [dispatch, id],
  );

  const handleFetchFailed = React.useCallback(
    (error: any) => dispatch(fetchLeaderboardFail(id, error)),
    [dispatch, id],
  );

  const handleFetchStart = React.useCallback(() => dispatch(fetchLeaderboard(id)), [dispatch, id]);

  const handleFetchAborted = React.useCallback(
    () => dispatch(fetchLeaderboardAbort(id)),
    [dispatch, id],
  );

  const pollingConfig: UsePollingParams<Q, LeaderboardListModel<T>> = React.useMemo(
    () => ({
      resolver,
      onStart: handleFetchStart,
      onFetchSucceed: handleFetchSuccess,
      onFetchFailed: handleFetchFailed,
      onFetchAborted: handleFetchAborted,
      pollingInterval,
      enabled,
      isPaused: underEdit,
    }),
    [
      resolver,
      handleFetchStart,
      handleFetchSuccess,
      handleFetchFailed,
      handleFetchAborted,
      pollingInterval,
      underEdit,
      enabled,
    ],
  );

  const retryConfig = React.useMemo(() => ({ initialInterval: 400 }), []);

  const { refresh } = usePolling(query, pollingConfig, retryConfig);

  const state = useSelector((state: AppState) => getLeaderboardState<T>(state, id));

  return {
    ...state,
    refresh,
  };
};
